import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Download, ExternalLink, Loader2, Maximize2, Minimize2 } from "@fg/icons";
import { driveApi, errorMessage, isAbortError, type DriveItem } from "@/api/driveApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useFocusTrap } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatBytes } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
    TEXT_PREVIEW_MAX_BYTES,
    officeKindOf,
    officeServiceUrl,
    openWithAppName,
    previewKindOf,
    categoryOf
} from "@/lib/fileTypes";
import { useDriveActions } from "../driveActions";

const PdfPreview = lazy(() => import("./PdfPreview"));
import { ItemIcon } from "../items/ItemIcon";

interface Props {
    list: DriveItem[];
    index: number;
    onIndexChange: (index: number) => void;
    onClose: () => void;
}

/**
 * Full-screen dark preview. ←/→ step through the list's files, Esc closes.
 * Images (fit / actual size), PDFs, video/audio (native players on a
 * download link), text/code ≤ 2 MB as plain text, .docx/.xlsx/.pptx via the
 * server-side ToPdf converters; everything else gets a download card.
 */
export function PreviewOverlay({ list, index, onIndexChange, onClose }: Props) {
    const rootRef = useRef<HTMLDivElement>(null);
    useFocusTrap(rootRef);
    const actions = useDriveActions();
    const item = list[index];
    const [zoom, setZoom] = useState<"fit" | "actual">("fit");

    useEffect(() => setZoom("fit"), [index]);

    const hasPrev = index > 0;
    const hasNext = index < list.length - 1;
    const go = (delta: number) => {
        const next = index + delta;
        if (next >= 0 && next < list.length) onIndexChange(next);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        const tag = (e.target as HTMLElement).tagName;
        if (e.key === "Escape") {
            e.preventDefault();
            onClose();
            return;
        }
        // Leave arrow keys to media controls and inputs.
        if (tag === "VIDEO" || tag === "AUDIO" || tag === "INPUT") return;
        if (e.key === "ArrowLeft") {
            e.preventDefault();
            go(-1);
        } else if (e.key === "ArrowRight") {
            e.preventDefault();
            go(1);
        }
    };

    const office = item ? officeKindOf(item) : null;
    // Docs / Sheets / Slides, or the PDF editor for PDFs.
    const appName = item ? openWithAppName(item) : null;
    const isImage = item ? previewKindOf(item) === "image" : false;
    const headerBtn = "flex h-10 w-10 items-center justify-center rounded-full text-zinc-200 transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400";

    return createPortal(
        <div
            ref={rootRef}
            role="dialog"
            aria-modal="true"
            aria-label={item ? `Preview of ${item.name}` : "Preview"}
            tabIndex={-1}
            data-drive-overlay=""
            onKeyDown={onKeyDown}
            className="drive-fade-in fixed inset-0 z-[55] flex flex-col bg-zinc-950/95 text-zinc-100 outline-none"
        >
            <header className="flex h-14 shrink-0 items-center gap-2 px-2 sm:px-4">
                <button type="button" onClick={onClose} aria-label="Close preview" title="Close (Esc)" className={headerBtn}>
                    <ArrowLeft className="h-5 w-5" />
                </button>
                {item ? <ItemIcon item={item} className="h-5 w-5" /> : null}
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium" title={item?.name}>{item?.name}</div>
                    {item ? (
                        <div className="truncate text-xs text-zinc-400">
                            {formatBytes(item.sizeBytes)}
                            {list.length > 1 ? ` · ${index + 1} of ${list.length}` : ""}
                        </div>
                    ) : null}
                </div>
                {item && isImage ? (
                    <button
                        type="button"
                        onClick={() => setZoom((z) => (z === "fit" ? "actual" : "fit"))}
                        aria-label={zoom === "fit" ? "Show actual size" : "Fit to screen"}
                        title={zoom === "fit" ? "Actual size" : "Fit to screen"}
                        className={headerBtn}
                    >
                        {zoom === "fit" ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
                    </button>
                ) : null}
                {item && appName && (office || item.canDownload !== false) ? (
                    <Button variant="secondary" size="sm" onClick={() => actions.openInApp(item)} className="hidden sm:inline-flex">
                        <ExternalLink className="h-4 w-4" /> Open in {appName}
                    </Button>
                ) : null}
                {item ? (
                    <button type="button" onClick={() => void actions.download([item])} aria-label={`Download ${item.name}`} title="Download" className={headerBtn}>
                        <Download className="h-5 w-5" />
                    </button>
                ) : null}
            </header>

            <div
                className="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-2 sm:px-16 sm:pb-6"
                onClick={(e) => {
                    if (e.target === e.currentTarget) onClose();
                }}
            >
                {item ? (
                    <PreviewContent key={`${item.id}:${item.updatedAt}`} item={item} zoom={zoom} onToggleZoom={() => setZoom((z) => (z === "fit" ? "actual" : "fit"))} />
                ) : null}
                {hasPrev ? (
                    <button
                        type="button"
                        onClick={() => go(-1)}
                        aria-label="Previous file"
                        title="Previous (←)"
                        className="absolute left-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-zinc-800/80 text-zinc-100 shadow-lg transition-colors hover:bg-zinc-700 sm:left-4"
                    >
                        <ChevronLeft className="h-6 w-6" />
                    </button>
                ) : null}
                {hasNext ? (
                    <button
                        type="button"
                        onClick={() => go(1)}
                        aria-label="Next file"
                        title="Next (→)"
                        className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-zinc-800/80 text-zinc-100 shadow-lg transition-colors hover:bg-zinc-700 sm:right-4"
                    >
                        <ChevronRight className="h-6 w-6" />
                    </button>
                ) : null}
            </div>
        </div>,
        document.body
    );
}

type Loaded =
    | { status: "loading" }
    | { status: "url"; url: string }
    | { status: "frame"; url: string }
    | { status: "pdf"; blob: Blob }
    | { status: "text"; text: string }
    | { status: "none"; reason: string };

function noneReason(item: DriveItem): string {
    const cat = categoryOf(item);
    if ((cat === "text" || cat === "code") && item.sizeBytes > TEXT_PREVIEW_MAX_BYTES) {
        return "This file is too large to preview.";
    }
    return "No preview available for this file type.";
}

function PreviewContent({ item, zoom, onToggleZoom }: { item: DriveItem; zoom: "fit" | "actual"; onToggleZoom: () => void }) {
    const token = useAccessToken();
    const kind = previewKindOf(item);
    const [state, setState] = useState<Loaded>(kind === "none" ? { status: "none", reason: noneReason(item) } : { status: "loading" });

    useEffect(() => {
        if (kind === "none") return;
        const ctrl = new AbortController();
        let objectUrl: string | null = null;
        let cancelled = false;
        const done = (s: Loaded) => {
            if (!cancelled) setState(s);
        };
        void (async () => {
            try {
                if (kind === "image" || kind === "video" || kind === "audio") {
                    const { url } = await driveApi.createDownloadLink([item.id], token);
                    done({ status: "url", url });
                    return;
                }
                const blob = await driveApi.fetchBlob(item.id, token, ctrl.signal);
                if (kind === "text") {
                    done({ status: "text", text: await blob.text() });
                    return;
                }
                if (kind === "pdf") {
                    // Painted by our own renderer: the browser's built-in viewer can come up blank (CSP, no PDF plug-in, mobile).
                    done({ status: "pdf", blob });
                    return;
                }
                const office = officeKindOf(item);
                const base = office ? officeServiceUrl(office) : undefined;
                if (!base) {
                    done({ status: "none", reason: "Previews for this file type aren't available here." });
                    return;
                }
                const form = new FormData();
                form.append("files", new File([blob], item.name, { type: blob.type || "application/octet-stream" }));
                // The converters are moving behind sign-in; any Fowlguard token carries their audience.
                const res = await fetch(`${base.replace(/\/$/, "")}/ToPdf`, {
                    method: "POST",
                    body: form,
                    signal: ctrl.signal,
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                    credentials: "omit"
                });
                if (!res.ok) throw new Error("conversion failed");
                done({ status: "pdf", blob: await res.blob() });
            } catch (err) {
                if (cancelled || isAbortError(err)) return;
                done({
                    status: "none",
                    reason: kind === "office" ? "We couldn't render a preview of this file." : errorMessage(err)
                });
            }
        })();
        return () => {
            cancelled = true;
            ctrl.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
        // item identity changes on refetch; the key on this component covers id+updatedAt.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind, token]);

    if (state.status === "loading") {
        return (
            <div className="flex items-center gap-2 text-sm text-zinc-300" role="status">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading preview…
            </div>
        );
    }
    if (state.status === "none") return <NoPreviewCard item={item} reason={state.reason} />;

    const failed = () => setState({ status: "none", reason: "This file couldn't be displayed." });

    if (state.status === "text") {
        return (
            <pre className="h-full w-full max-w-5xl overflow-auto whitespace-pre-wrap break-words rounded-lg bg-white p-4 font-mono text-[13px] leading-relaxed text-zinc-900 shadow-2xl sm:p-6">
                {state.text}
            </pre>
        );
    }
    if (state.status === "pdf") {
        return (
            <Suspense fallback={<div className="flex items-center gap-2 text-sm text-zinc-300" role="status"><Loader2 className="h-5 w-5 animate-spin" /> Loading preview…</div>}>
                <PdfPreview blob={state.blob} name={item.name} />
            </Suspense>
        );
    }
    if (state.status === "frame") {
        return <iframe title={item.name} src={state.url} className="h-full w-full max-w-6xl rounded-lg bg-white shadow-2xl" />;
    }
    // url: image / video / audio
    if (kind === "image") {
        return zoom === "fit" ? (
            <img
                src={state.url}
                alt={item.name}
                onClick={onToggleZoom}
                onError={failed}
                className="max-h-full max-w-full cursor-zoom-in object-contain shadow-2xl"
            />
        ) : (
            <div className="flex h-full w-full overflow-auto">
                <img src={state.url} alt={item.name} onClick={onToggleZoom} onError={failed} className="m-auto max-w-none cursor-zoom-out" />
            </div>
        );
    }
    if (kind === "video") {
        return <video src={state.url} controls autoPlay playsInline onError={failed} className="max-h-full max-w-full rounded-lg bg-black shadow-2xl" />;
    }
    return (
        <div className="flex w-full max-w-md flex-col items-center gap-5 rounded-2xl bg-zinc-900 p-8 shadow-2xl">
            <ItemIcon item={item} className="h-16 w-16" />
            <div className="w-full truncate text-center text-sm font-medium">{item.name}</div>
            <audio src={state.url} controls autoPlay onError={failed} className="w-full" />
        </div>
    );
}

function NoPreviewCard({ item, reason }: { item: DriveItem; reason: string }) {
    const actions = useDriveActions();
    const office = officeKindOf(item);
    const appName = openWithAppName(item);
    return (
        <div className="flex w-full max-w-sm flex-col items-center rounded-2xl bg-zinc-900 px-6 py-10 text-center shadow-2xl">
            <ItemIcon item={item} className="h-16 w-16" />
            <div className="mt-4 w-full truncate text-base font-medium" title={item.name}>{item.name}</div>
            <p className={cn("mt-1 text-sm text-zinc-400")}>{reason}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
                <Button onClick={() => void actions.download([item])}>
                    <Download className="h-4 w-4" /> Download
                </Button>
                {appName && (office || item.canDownload !== false) ? (
                    <Button variant="secondary" onClick={() => actions.openInApp(item)}>
                        <ExternalLink className="h-4 w-4" /> Open in {appName}
                    </Button>
                ) : null}
            </div>
        </div>
    );
}
